const A = 6378137.0; // WGS84 semi-major axis
const F = 1 / 298.257223563;
const E2 = F * (2 - F);

const ORIGIN_LAT = 50.08; // Prague — must match LAT/LNG in SkyScene
const ORIGIN_LON = 14.44;

const toEcef = (latDeg: number, lonDeg: number, height: number) => {
  const lat = (latDeg * Math.PI) / 180;
  const lon = (lonDeg * Math.PI) / 180;
  const sinLat = Math.sin(lat);
  const cosLat = Math.cos(lat);
  const n = A / Math.sqrt(1 - E2 * sinLat * sinLat);
  return {
    x: (n + height) * cosLat * Math.cos(lon),
    y: (n + height) * cosLat * Math.sin(lon),
    z: (n * (1 - E2) + height) * sinLat,
  };
};

const origin = toEcef(ORIGIN_LAT, ORIGIN_LON, 0);
const oLat = (ORIGIN_LAT * Math.PI) / 180;
const oLon = (ORIGIN_LON * Math.PI) / 180;
const sinLat0 = Math.sin(oLat);
const cosLat0 = Math.cos(oLat);
const sinLon0 = Math.sin(oLon);
const cosLon0 = Math.cos(oLon);

export const geodeticToScene = (
  latDeg: number,
  lonDeg: number,
  height: number
): { x: number; y: number; z: number } => {
  const p = toEcef(latDeg, lonDeg, height);
  const dx = p.x - origin.x;
  const dy = p.y - origin.y;
  const dz = p.z - origin.z;
  return {
    // north
    x: -sinLat0 * cosLon0 * dx - sinLat0 * sinLon0 * dy + cosLat0 * dz,
    // up
    y: cosLat0 * cosLon0 * dx + cosLat0 * sinLon0 * dy + sinLat0 * dz,
    // east
    z: -sinLon0 * dx + cosLon0 * dy,
  };
};

export const bearingOf = (x: number, z: number): number =>
  ((Math.atan2(z, x) * 180) / Math.PI + 360) % 360;

export const CARDINALS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

export const cardinalFor = (bearing: number): string =>
  CARDINALS[Math.round(bearing / 45) % 8];

export interface FlightState {
  id: string;
  callsign: string | null;
  country: string | null;
  lat: number;
  lon: number;
  alt: number;
  velocity: number | null;
  heading: number | null;
  vertRate: number | null;
  /** unix seconds the fix was measured */
  t: number;
}

export const advanceFlight = (f: FlightState, seconds: number) => {
  if (f.velocity == null || f.heading == null) {
    return { lat: f.lat, lon: f.lon };
  }
  const d = f.velocity * seconds;
  const hdg = (f.heading * Math.PI) / 180;
  return {
    lat: f.lat + (d * Math.cos(hdg)) / 111320,
    lon:
      f.lon +
      (d * Math.sin(hdg)) / (111320 * Math.cos((f.lat * Math.PI) / 180) || 1),
  };
};

/**
 * Where a point lands on screen *after* the fisheye pass.
 *
 * The effect is a sampling remap — output uv samples input at
 * `c * (1 - k|c|^2) / zoom` — so finding where a known scene point appears
 * means inverting it. Newton-ish iteration converges in a few passes at the
 * small strengths used here.
 */
export const applyFisheye = (
  ndcX: number,
  ndcY: number,
  strength: number,
  zoom: number,
  width: number,
  height: number
): { x: number; y: number } => {
  if (Math.abs(strength) < 1e-4 && Math.abs(zoom - 1) < 1e-4) {
    return { x: ndcX, y: ndcY };
  }
  const ax = width >= height ? width / height : 1;
  const ay = width >= height ? 1 : height / width;
  const cx = ndcX * 0.5 * ax;
  const cy = ndcY * 0.5 * ay;
  const s = Math.hypot(cx, cy);
  if (s < 1e-6) return { x: ndcX, y: ndcY };

  let r = zoom * s;
  for (let i = 0; i < 4; i++) r = zoom * s + strength * r * r * r;

  const k = r / s;
  return { x: (cx * k * 2) / ax, y: (cy * k * 2) / ay };
};
